import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Cómo encuentro un proveedor legítimo de SAINT®?",
    answer:
      "SAINT® es un protocolo autorizado por la FDA (K220177). Busque un proveedor que lo administre según el protocolo, con orientación por fMRI y un equipo clínico que evalúe la idoneidad. [[Encuentre un proveedor verificado de SAINT®|/es/find-a-verified-saint-provider]].",
  },
  {
    question: "¿SAINT® requiere orientación por fMRI?",
    answer:
      "El protocolo SAINT® usa una resonancia magnética funcional (fMRI) para orientar la estimulación al circuito de cada persona antes de iniciar el tratamiento. Pregunte al proveedor si sigue el protocolo completo.",
  },
];

export const esHowToFindLegitimateSaintProvider = makeEsArticle({
  slug: "how-to-find-a-legitimate-saint-provider",
  title: "Cómo encontrar un proveedor legítimo de SAINT® | Neuro Wellness",
  description:
    "Cómo verificar un proveedor de SAINT®: protocolo autorizado por la FDA (K220177), orientación por fMRI y evaluación clínica de la idoneidad.",
  h1: "Cómo encontrar un proveedor legítimo de SAINT®",
  breadcrumbLabel: "Proveedor legítimo de SAINT®",
  shortAnswer:
    "SAINT® es un protocolo autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. Para encontrar un proveedor legítimo, verifique que administre el protocolo completo (orientación por fMRI, 50 sesiones en cinco días, estimulación theta burst) y que cuente con un equipo clínico que evalúe la idoneidad. [[Encuentre un proveedor verificado de SAINT®|/es/find-a-verified-saint-provider]] o llame al 954-827-9793.",
  keyTakeaways: [
    "SAINT® está autorizado por la FDA (K220177).",
    "El protocolo completo usa orientación por fMRI.",
    "50 sesiones en cinco días.",
    "Un equipo clínico evalúa la idoneidad.",
    "Verifique que el proveedor siga el protocolo completo.",
  ],
  sections: [
    {
      heading: "Qué buscar en un proveedor de SAINT®",
      paragraphs: [
        "Un proveedor legítimo de SAINT® administra el protocolo autorizado por la FDA (K220177): orientación por fMRI, 50 sesiones en cinco días y estimulación theta burst. Debe contar con un equipo clínico que evalúe la idoneidad mediante una evaluación psiquiátrica.",
      ],
    },
    {
      heading: "Cómo verificar",
      paragraphs: [
        "Pregunte si el proveedor sigue el protocolo completo, incluida la orientación por fMRI. SAINT® está indicado para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. [[Encuentre un proveedor verificado de SAINT®|/es/find-a-verified-saint-provider]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esWhatToLookForInDepressionCenter = makeEsArticle({
  slug: "what-to-look-for-in-a-depression-treatment-center",
  title:
    "Qué buscar en un centro de tratamiento para la depresión | Neuro Wellness",
  description:
    "Qué buscar en un centro de tratamiento para la depresión: tratamientos autorizados por la FDA, equipo clínico, evaluación psiquiátrica y atención centrada en el paciente.",
  h1: "Qué buscar en un centro de tratamiento para la depresión",
  breadcrumbLabel: "Centro de tratamiento para la depresión",
  shortAnswer:
    "Al elegir un centro de tratamiento para la depresión, busque tratamientos autorizados o aprobados por la FDA (TMS, SAINT®, SPRAVATO®), un equipo clínico con psiquiatra, una evaluación psiquiátrica integral antes del tratamiento y atención centrada en el paciente. SAINT® está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos.",
  keyTakeaways: [
    "Busque tratamientos autorizados o aprobados por la FDA.",
    "El centro debe contar con un psiquiatra.",
    "Debe realizar una evaluación psiquiátrica antes del tratamiento.",
    "La atención debe ser centrada en el paciente.",
  ],
  sections: [
    {
      heading: "Tratamientos autorizados por la FDA",
      paragraphs: [
        "Un centro serio ofrece tratamientos autorizados o aprobados por la FDA: TMS, SAINT® (K220177) y SPRAVATO®. Evite tratamientos experimentales presentados como curas garantizadas.",
      ],
    },
    {
      heading: "Equipo clínico y evaluación",
      paragraphs: [
        "El centro debe contar con un psiquiatra y realizar una evaluación psiquiátrica integral antes del tratamiento. La idoneidad se confirma caso por caso. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
